import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Button, Loader, Menu, Text, ThemeIcon, Tooltip } from "@mantine/core"
import { IconCheck, IconChevronDown, IconSparkles } from "@tabler/icons-react"
import { useMemo } from "react"
import type { IProps } from "./interfaces"

import classes from "./styles.module.css"

/**
 * ReportOfferBanner — the two-state offer that surfaces a visual report next to
 * the meeting summary.
 *
 * - **Suggested** (nothing generated, 0 credits): a brand-tinted suggestion
 *   callout — sparkle + "This looked like a {type} meeting" title, one calm
 *   sentence, then an action row: the primary "Generate · ~N cr" button with a
 *   best-model toggle SHELF fused to its bottom edge, and a ghost "Choose
 *   another type" menu. The button reserves the width of the widest tier price
 *   so toggling tiers never resizes it.
 * - **Ready** (a report exists): "View report" + a "Generate another" menu.
 *
 * Renders `null` when the meeting isn't report-worthy and nothing is generated —
 * no banner, no dead buttons. Dumb: all data + handlers arrive via props.
 */
export const ReportOfferBanner = ({
  fit,
  reportTypes,
  generated,
  estimateCredits,
  bestAllowed,
  bestEstimateCredits,
  useBestModel,
  onToggleBestModel,
  isGenerating,
  generatingType,
  onGenerate,
  onView,
}: IProps): React.JSX.Element | null => {
  // EXTERNAL HOOKS
  const { i18n } = useLingui()

  // DERIVED STATE
  const suggestedType = fit?.suggestedType ?? null
  // The report to offer for viewing: prefer one matching the suggested type,
  // else the most-recently generated (the list arrives newest-first).
  const readyReport = useMemo(
    () => generated.find((g) => g.reportType === suggestedType) ?? generated[0] ?? null,
    [generated, suggestedType],
  )
  const suggestedLabel = useMemo(
    () => reportTypes.find((rt) => rt.id === suggestedType)?.label ?? suggestedType ?? "",
    [reportTypes, suggestedType],
  )
  const otherTypes = useMemo(() => reportTypes.filter((rt) => rt.id !== suggestedType), [reportTypes, suggestedType])

  // GUARD — not report-worthy and nothing generated ⇒ no banner.
  if (!fit?.eligible || (!suggestedType && generated.length === 0)) return null

  // READY — a report has been generated. Same callout language as the
  // suggested state: title row, one calm sentence, action row underneath.
  if (readyReport) {
    return (
      <section className={classes.callout} data-testid="report-offer-ready">
        {/* ThemeIcon renders a div, which is invalid inside a p. */}
        <Text component="div" fw={700} className={classes.calloutTitle}>
          <ThemeIcon variant="light" color="success" size="sm" radius="xl" className={classes.readyIco}>
            <IconCheck size={14} />
          </ThemeIcon>
          <span className={classes.calloutTitleText}>{readyReport.title}</span>
        </Text>
        <Text component="p" size="sm" className={classes.calloutDesc}>
          <Trans>Your visual report is ready to view.</Trans>
        </Text>
        <div className={classes.actionRow}>
          <Button color="brand" className={classes.generate} onClick={() => onView(readyReport.artifactId)}>
            <Trans>View report</Trans>
          </Button>
          <Menu shadow="md" position="bottom-start" withinPortal>
            <Menu.Target>
              <Button
                variant="default"
                className={classes.ghost}
                rightSection={<IconChevronDown size={14} />}
                loading={isGenerating}
              >
                <Trans>Generate another</Trans>
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              {reportTypes.map((rt) => (
                <Menu.Item key={rt.id} onClick={() => onGenerate(rt.id)}>
                  {rt.label}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        </div>
      </section>
    )
  }

  // SUGGESTED — nothing generated yet. `suggestedType` is guaranteed here (the
  // guard rules out "no suggestion and nothing generated"), but narrow for TS.
  if (!suggestedType) return null

  // This client is watching its OWN generation of the suggested type — swap the
  // wide "Generate · ~N cr" CTA for a compact, labelled spinner so the button
  // never renders as a bare loader floating in an over-wide pill.
  const isGeneratingSuggested = isGenerating && generatingType === suggestedType
  const showBestToggle = !!bestAllowed && !!onToggleBestModel
  // The Generate button always reflects the credits for the ACTIVE tier…
  const activeCredits = useBestModel ? bestEstimateCredits : estimateCredits
  // …but is laid out for the WIDEST tier price, so flipping the best-model
  // shelf never changes the button's size (an invisible sizer reserves it).
  const sizerCredits = showBestToggle
    ? Math.max(estimateCredits ?? 0, bestEstimateCredits ?? 0) || activeCredits
    : activeCredits

  return (
    <section className={classes.callout} data-testid="report-offer-suggested">
      <Text component="p" fw={700} className={classes.calloutTitle}>
        <IconSparkles size={16} className={classes.sparkle} aria-hidden="true" />
        <span className={classes.calloutTitleText}>
          <Trans>
            This looked like a <span className={classes.suggestType}>{suggestedLabel}</span> meeting
          </Trans>
        </span>
      </Text>
      <Text component="p" size="sm" className={classes.calloutDesc}>
        <Trans>Turn it into a visual report — no credits spent yet.</Trans>
      </Text>
      <div className={classes.actionRow}>
        <div className={classes.genStack}>
          <Button
            color="brand"
            className={classes.generate}
            data-shelved={showBestToggle || undefined}
            data-busy={isGeneratingSuggested || undefined}
            onClick={isGeneratingSuggested ? undefined : () => onGenerate(suggestedType)}
            leftSection={isGeneratingSuggested ? <Loader size={16} color="white" /> : undefined}
            disabled={isGenerating && !isGeneratingSuggested}
          >
            {isGeneratingSuggested ? (
              <Trans>Generating…</Trans>
            ) : activeCredits != null && sizerCredits != null ? (
              <Trans>
                Generate ·{" "}
                <span className={classes.crNum}>
                  <span className={classes.crSizer} aria-hidden="true">
                    ~{sizerCredits}
                  </span>
                  <span className={classes.crVal}>~{activeCredits}</span>
                </span>{" "}
                cr
              </Trans>
            ) : (
              <Trans>Generate</Trans>
            )}
          </Button>
          {showBestToggle && (
            <Tooltip
              label={i18n._(msg`Sharper synthesis and richer diagrams — worth it for the meetings that matter.`)}
              multiline
              w={240}
              withArrow
              position="bottom-start"
            >
              <button
                type="button"
                role="switch"
                aria-checked={useBestModel ?? false}
                className={classes.shelf}
                data-on={useBestModel || undefined}
                disabled={isGenerating}
                onClick={() => onToggleBestModel?.(!useBestModel)}
              >
                <span className={classes.miniSwitch} aria-hidden="true" />
                <Trans>Best model</Trans>
                <span className={classes.shelfFactor}>~5×</span>
              </button>
            </Tooltip>
          )}
        </div>
        {otherTypes.length > 0 && (
          <Menu shadow="md" position="bottom-start" withinPortal>
            <Menu.Target>
              <Button
                variant="default"
                className={classes.ghost}
                rightSection={<IconChevronDown size={14} />}
                disabled={isGenerating}
              >
                <Trans>Choose another type</Trans>
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              {otherTypes.map((rt) => (
                <Menu.Item key={rt.id} onClick={() => onGenerate(rt.id)}>
                  {rt.label}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        )}
      </div>
    </section>
  )
}
